import React, { Component } from "react";
import ReactAutosuggest from "react-autosuggest";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import TextField from "@material-ui/core/TextField";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import { withStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import PropTypes from "prop-types";

const styles = (theme) => ({
  container: {
    position: "relative",
    marginBottom: 20,
  },
  suggestionsContainerOpen: {
    position: "absolute",
    zIndex: 3,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  suggestion: {
    display: "block",
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: "none",
  },
  divider: {
    height: theme.spacing(2),
  },
  autosuggest: {
    marginTop: 5,
  },
  suggestionItem: {
    textOverflow: "ellipsis",
  },
});
export class Autosuggest extends Component {
  static propTypes = {
    /** The data to derive suggestions from */
    data: PropTypes.array.isRequired,
    /** A function that returns a React element to render in the suggestion list.
     * If not provided, renders a <MenuItem /> with the data's label
     */
    renderSuggestion: PropTypes.func,
    /** The label to show in the text input */
    inputLabel: PropTypes.string.isRequired,
    /** A function that returns true if the suggestion should be kept,
     * or false if not. If not provided, returns true if the suggestion's
     * label matches the input text
     */
    keepSuggestion: PropTypes.func,
    /** A function that returns a string to display in the text
     * field when an item is selected
     */
    getSuggestionValue: PropTypes.func,
    /** The key to use for the value of the suggestion. If not provided,
     *  defaults to 'label'.
     */
    objectKey: PropTypes.string,
  };

  static defaultProps = {
    objectKey: "label",
  };

  state = {
    instance: new Date().getTime().toString(),
    activeNode: null,
    suggestions: [],
    input: "",
    loading: false,
  };
  debounce = null;

  render() {
    const { classes } = this.props;
    return (
      <div>
        <ReactAutosuggest
          className={classes.autosuggest}
          renderInputComponent={this.renderInputComponent}
          suggestions={this.state.suggestions}
          onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          onSuggestionSelected={this.onSuggestionSelected}
          inputProps={{
            classes,
            placeholder: this.props.inputLabel,
            value: this.state.input,
            onChange: this.handeInputChange,
            disabled: this.props.disabled,
          }}
          theme={{
            container: classes.container,
            suggestionsContainerOpen: classes.suggestionsContainerOpen,
            suggestionsList: classes.suggestionsList,
            suggestion: classes.suggestion,
          }}
          renderSuggestionsContainer={(options) => (
            <Paper {...options.containerProps} square>
              {options.children}
            </Paper>
          )}
        />
      </div>
    );
  }

  onSuggestionSelected = (
    e,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }
  ) => {
    if (this.props.onSuggestionSelected) {
      return this.props.onSuggestionSelected(e, {
        suggestion,
        suggestionValue,
        suggestionIndex,
        sectionIndex,
        method,
      });
    }
    if (this.props.clearOnSelect) {
      this.setState({ input: "" });
    } else {
      this.setState({ input: suggestionValue });
    }
  };

  renderSuggestion = (suggestion, { query, isHighlighted }) => {
    if (this.props.renderSuggestion) {
      return this.props.renderSuggestion(suggestion, { query, isHighlighted });
    }

    const matches = match(suggestion[this.props.objectKey], query);
    const parts = parse(suggestion[this.props.objectKey], matches);

    return (
      <MenuItem selected={isHighlighted} component="div">
        <div>
          {parts.map(
            (part, index) =>
              part.highlight ? (
                <span key={String(index)} style={{ fontWeight: 500 }}>
                  {part.text}
                </span>
              ) : (
                <strong key={String(index)} style={{ fontWeight: 300 }}>
                  {part.text}
                </strong>
              )
          )}
        </div>
      </MenuItem>
    );
  };

  getSuggestions = async (value) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    let count = 0;
    let data = null;

    if (this.props.getSuggestions && inputLength > 0) {
      data = await this.props.getSuggestions(inputValue);
      return data;
    } else {
      if (this.props.data.content != null) {
        data = this.props.data.content;
      } else {
        data = this.props.data;
      }
    }
    return inputLength === 0
      ? []
      : data.filter((user) => {
          const keep = count < 5 && this.keepSuggestion(user, inputValue);

          if (keep) {
            count += 1;
          }

          return keep;
        });
  };

  keepSuggestion = (suggestion, input) => {
    if (this.props.keepSuggestion) {
      return this.props.keepSuggestion(suggestion, input);
    }
    return (
      suggestion[this.props.objectKey].slice(0, input.length).toLowerCase() ===
      input
    );
  };

  renderInputComponent = (inputProps) => {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;

    return (
      <>
        <TextField
          aria-label="Autosuggest input"
          fullWidth
          InputProps={{
            inputRef: (node) => {
              ref(node);
              inputRef(node);
            },
            classes: {
              input: classes.input,
            },
          }}
          {...other}
        />
        {this.state.loading && (
          <LinearProgress color="secondary" variant="indeterminate" />
        )}
      </>
    );
  };

  getSuggestionValue = (suggestion) => {
    if (this.props.getSuggestionValue) {
      return this.props.getSuggestionValue(suggestion);
    }
    return suggestion[this.props.objectKey];
  };

  handleSuggestionsFetchRequested = ({ value }) => {
    this.setState({ loading: true });
    if (this.debounce) {
      window.clearTimeout(this.debounce);
    }
    this.debounce = window.setTimeout(() => {
      this.getSuggestions(value)
        .then((suggestions) => {
          this.setState({
            suggestions,
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
      this.debounce = null;
    }, 300);
  };

  handleSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  handeInputChange = (event, { newValue }) => {
    this.setState({
      input: newValue,
    });
  };
}

export default withStyles(styles)(Autosuggest);
